import apiClient from './apiClient';

const superAdminService = {
  // Tenant Management
  async getTenants(options = {}) {
    const params = buildTenantQueryParams(options)
    const response = await apiClient.get('/api/super-admin/tenants', { params })
    return response.data
  },

  async updateTenant(tenantData) {
    const response = await apiClient.put('/api/super-admin/tenants', tenantData);
    return response.data;
  },

  async deleteTenant(tenantId) {
    const response = await apiClient.delete(`/api/super-admin/tenants/${tenantId}`);
    return response.data;
  },

  async updateCompanies(tenantId) {
    const response = await apiClient.post(`/api/super-admin/tenants/${tenantId}/fetch-companies`);
    return response.data;
  },

  // User Management
  async getUsers(searchTerm = '', tenantId = '', page = 1, pageSize = 10, options = {}) {
    const params = {};
    if (searchTerm) params.searchText = searchTerm;
    if (tenantId) params.tenantId = tenantId;
    params.page = page;
    params.pageSize = pageSize;
    
    // Add sorting if provided
    if (options.sortField && options.sortDirection) {
      params.sorting = `${options.sortField} ${options.sortDirection}`;
    }
    
    const response = await apiClient.get('/api/super-admin/users', { params });
    return response.data;
  },



  async updateUser(userData) {
    const response = await apiClient.put('/api/super-admin/users', userData);
    return response.data;
  },

  async deleteUser(userId) {
    const response = await apiClient.delete(`/api/super-admin/users/${userId}`);
    return response.data;
  },

  async sendPasswordReset(userId) {
    const response = await apiClient.post('/api/super-admin/users/send-password-reset', { userId });
    return response.data;
  },

  async resendEmailVerification(userEmail) {
    const response = await apiClient.post(`/api/supplier/account/resend-email-verification?email=${encodeURIComponent(userEmail)}`);
    return response.data;
  },

  // Permission Management
  async getUserPermissions(userId) {
    const response = await apiClient.get(`/api/super-admin/users/${userId}/permissions`);
    return response.data;
  },

  async grantPermission(userId, permissionName) {
    const response = await apiClient.post('/api/super-admin/users/permissions/grant', {
      userId,
      permissionName
    });
    return response.data;
  },

  async revokePermission(userId, permissionName) {
    const response = await apiClient.post('/api/super-admin/users/permissions/revoke', {
      userId,
      permissionName
    });
    return response.data;
  },

  // Dashboard Stats
  async getDashboardStats() {
    const response = await apiClient.get('/api/super-admin/dashboard/stats');
    return response.data;
  },

  // Login country statistics for Super Admin dashboard
  async getLoginCountryStatistics() {
    const response = await apiClient.get('/api/supplier/account/login-country-statistics');
    return response.data;
  },

  // Help Document Management
  async updateHelpDocument(editionId, language, file) {
    const formData = new FormData();
    formData.append('EditionId', editionId);
    formData.append('Culture', language);
    formData.append('File', file);

    const response = await apiClient.post('/api/super-admin/setup/update-help-document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Quotation Errors Management
  async getQuotationErrors(page = 1, pageSize = 10, sorting = 'LastErrorOccurrenceTime DESC') {
    const params = {
      Page: page,
      PageSize: pageSize,
      Sorting: sorting
    };
    const response = await apiClient.get('/api/super-admin/quotations/errors', { params });
    return response.data;
  },

  async deleteQuotationError(errorId) {
    const response = await apiClient.delete(`/api/super-admin/quotations/errors/${errorId}`);
    return response.data;
  },

  async resendQuotationError(errorId) {
    const response = await apiClient.post(`/api/super-admin/quotations/errors/${errorId}/resend`);
    return response.data;
  },

  // Import Log Errors Management
  async getImportLogErrors(page = 1, pageSize = 10, sorting = 'CreationTime DESC') {
    const params = {
      Page: page,
      PageSize: pageSize,
      Sorting: sorting
    };
    const response = await apiClient.get('/api/super-admin/import-logs/errors', { params });
    return response.data;
  },

  async deleteImportLogError(errorId) {
    const response = await apiClient.delete(`/api/super-admin/import-logs/errors/${errorId}`);
    return response.data;
  },

  async retryImportLogError(errorId) {
    const response = await apiClient.post(`/api/super-admin/import-logs/errors/${errorId}/retry`);
    return response.data;
  },

  // User Message Management
  async searchUserMessages(searchParams) {
    const response = await apiClient.post('/api/supplier/super-admin-user-message/search', searchParams);
    return response.data;
  },

  async getUserMessage(id) {
    const response = await apiClient.get(`/api/supplier/super-admin-user-message/${id}`);
    return response.data;
  },

  async createUserMessage(data) {
    const response = await apiClient.post('/api/supplier/super-admin-user-message', data);
    return response.data;
  },

  async updateUserMessage(id, data) {
    const response = await apiClient.put(`/api/supplier/super-admin-user-message/${id}`, data);
    return response.data;
  },

  async deleteUserMessage(id) {
    const response = await apiClient.delete(`/api/supplier/super-admin-user-message/${id}`);
    return response.data;
  },

  // PDF Template Management (PrintTemplate API)
  async searchPdfTemplates(searchParams = {}) {
    // Keep the same search pattern as other modules: POST with a body that includes
    // page, pageSize, searchText, sorting, etc.
    const response = await apiClient.post('/api/supplier/print-template/search', searchParams);
    return response.data;
  },

  async getPdfTemplate(id) {
    const response = await apiClient.get(`/api/supplier/print-template/${id}`);
    return response.data;
  },

  async createPdfTemplate(data) {
    const response = await apiClient.post('/api/supplier/print-template', data);
    return response.data;
  },

  async updatePdfTemplate(id, data) {
    const response = await apiClient.put(`/api/supplier/print-template/${id}`, data);
    return response.data;
  },

  async deletePdfTemplate(id) {
    const response = await apiClient.delete(`/api/supplier/print-template/${id}`);
    return response.data;
  },

  // Optional helper endpoints (implement on backend if needed)
  async uploadPdfTemplateFile(id, file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
      `/api/supplier/super-admin-pdf-template/${id}/file`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  },

  async getPdfTemplateTableNames() {
    const response = await apiClient.get('/api/supplier/print-template/table-names');
    return response.data;
  }
};

export default superAdminService; 

function buildTenantQueryParams(options) {
  const params = {}

  if (typeof options === 'string') {
    addTrimmedParam(params, 'search', options)
    return params
  }

  const opts = typeof options === 'object' && options ? options : {}

  addTrimmedParam(params, 'searchText', opts.searchText)
  addTrimmedParam(params, 'search', opts.search)
  addTrimmedParam(params, 'sorting', opts.sorting)
  
  // Add editionId if provided
  if (opts.editionId) {
    params.editionId = opts.editionId
  }
  
  // Add excludeEditionId if provided
  if (opts.excludeEditionId) {
    params.excludeEditionId = opts.excludeEditionId
  }

  addNumericParam(params, 'page', opts.page)
  addNumericParam(params, 'pageSize', opts.pageSize)

  addPagingParams(params, opts)

  return params
}

function addTrimmedParam(target, key, value) {
  if (typeof value !== 'string') return
  const trimmed = value.trim()
  if (trimmed) {
    target[key] = trimmed
  }
}

function addNumericParam(target, key, value) {
  if (Number.isInteger(value)) {
    target[key] = value
  }
}

function addPagingParams(target, opts) {
  if (Number.isInteger(opts.skipCount)) {
    target.skipCount = opts.skipCount
  } else if (Number.isInteger(opts.page) && Number.isInteger(opts.pageSize)) {
    target.skipCount = Math.max(opts.page - 1, 0) * opts.pageSize
  }

  if (Number.isInteger(opts.pageSize)) {
    target.pageSize = opts.pageSize
  } else if (Number.isInteger(opts.pageSize)) {
    target.pageSize = opts.pageSize
  }
}